'use client';
import { useQuery, useSuspenseQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { REVIEW_QUERIES } from '../../infrastructure/review/utils/queries';
import { REVIEW_MUTATIONS } from '../../infrastructure/review/utils/mutations';
import type {
  GetProductReviewsRequest,
  GetAIProductReviewsRequest,
  GetCuratedReviewsRequest,
  ReviewStatisticsRequest,
} from '../../infrastructure/review/utils/types';

export const useReviewService = () => {
  const queryClient = useQueryClient();

  const useGetProductReviews = (productId: number, params?: Omit<GetProductReviewsRequest, 'productId'>) => {
    return useQuery({
      ...REVIEW_QUERIES.getProductReviews(productId, params),
      enabled: Boolean(productId),
    });
  };

  const useGetAIProductReviews = (productId: number, params?: Omit<GetAIProductReviewsRequest, 'productId'>) => {
    return useQuery({
      ...REVIEW_QUERIES.getAIProductReviews(productId, params),
    });
  };

  const useGetCuratedReviews = (params?: GetCuratedReviewsRequest) => {
    return useSuspenseQuery(REVIEW_QUERIES.getCuratedReviews(params));
  };

  const useGetReviewStatistics = (params: ReviewStatisticsRequest) => {
    return useQuery({
      ...REVIEW_QUERIES.getReviewStatistics(params),
      enabled: Boolean(params.productIds),
    });
  };

  const useCreateReview = () => {
    return useMutation(REVIEW_MUTATIONS.createReview(queryClient));
  };

  return {
    useGetProductReviews,
    useGetAIProductReviews,
    useGetCuratedReviews,
    useGetReviewStatistics,
    useCreateReview,
  };
};
